/**
 * View component for displaying starred leads
 * Shows only the leads that the current user has starred
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Lead } from '../../types/Lead';
import { StarredLead } from '../../types/Star';
import { useAuth } from '../../contexts/AuthContext';

import LeadCard from '../leads/LeadCard';
import { Pagination } from '../ui/Pagination';

interface StarredLeadsViewProps {
  allLeads: Lead[];
  onLeadToggle: (leadId: string) => void;
  expandedCards: Set<string>;
  onMemberChange?: (leadId: string, isMember: boolean) => void;
  onStarChange?: (leadId: string, isStarred: boolean) => void;
  starredLeadIds: Set<string>;
  starredLeads: StarredLead[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalPages: number;
  itemsPerPage: number;
}

export const StarredLeadsView: React.FC<StarredLeadsViewProps> = ({
  allLeads,
  onLeadToggle,
  expandedCards,
  onMemberChange,
  onStarChange,
  starredLeadIds,
  starredLeads,
  currentPage,
  onPageChange,
  totalPages,
  itemsPerPage
}) => {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Load starred status for member leads
  const loadStarredStatus = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      // Just mark as not loading since we're using Dashboard's state
      setIsLoading(false);
    } catch (err) {
      console.error('Failed to load starred status:', err);
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    // If there are no leads at all, don't show loading state
    if (allLeads.length === 0) {
      setIsLoading(false);
      return;
    }
    loadStarredStatus();
  }, [loadStarredStatus, allLeads.length]);

  // Handle star changes
  const handleStarChange = useCallback((leadId: string, isStarred: boolean) => {
    console.log(`🔄 StarredLeadsView: Star change received: leadId=${leadId}, isStarred=${isStarred}`);
    console.log(`📊 StarredLeadsView: Current starredLeadIds:`, Array.from(starredLeadIds));
    console.log(`📊 StarredLeadsView: Current starredLeads count:`, starredLeads.length);

    if (isStarred) {
      // Lead was starred - refresh the list to include it
      console.log(`✅ StarredLeadsView: Lead ${leadId} was starred, refreshing list...`);
      loadStarredStatus();
    } else {
      // Lead was unstarred - remove it from the list
      console.log(`❌ StarredLeadsView: Lead ${leadId} was unstarred, removing from list...`);
      // Note: We don't need to manually remove it since the Dashboard will update starredLeadIds
      // and this component will re-render with the updated data
    }

    // Always notify the Dashboard about the star change
    onStarChange?.(leadId, isStarred);
  }, [loadStarredStatus, onStarChange, starredLeadIds, starredLeads.length]);

  // Filter leads to only show starred ones
  const filteredLeads = allLeads.filter(lead => starredLeadIds.has(lead.id || ''));

  // Sort by starred date (most recently starred first)
  const sortedLeads = filteredLeads.sort((a, b) => {
    const aStarred = starredLeads.find(star => star.leadId === a.id);
    const bStarred = starredLeads.find(star => star.leadId === b.id);

    if (!aStarred || !bStarred) return 0;

    return new Date(bStarred.starredAt).getTime() - new Date(aStarred.starredAt).getTime();
  });



  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px',
        color: '#6b7280'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '16px'
        }}>
          <div style={{
            width: '20px',
            height: '20px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid #667eea',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          Loading starred leads...
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  if (sortedLeads.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '48px',
        color: '#6b7280'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '32px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '2px dashed #d1d5db',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⭐</div>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
            No Starred Leads
          </div>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Star leads you want to revisit later by clicking the star icon on any lead card.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        marginBottom: '24px',
        padding: '16px',
        backgroundColor: '#fffbeb',
        borderRadius: '8px',
        border: '1px solid #fbbf24',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px' }}>⭐</span>
        <div>
          <div style={{ fontWeight: '600', color: '#92400e' }}>
            Starred Leads ({sortedLeads.length})
          </div>
          <div style={{ fontSize: '14px', color: '#a16207' }}>
            These are leads you've marked for later review
          </div>
        </div>
      </div>

      {sortedLeads.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((lead) => (
        <LeadCard
          key={lead.id}
          lead={lead}
          isExpanded={expandedCards.has(lead.id || '')}
          onToggle={() => lead.id && onLeadToggle(lead.id)}
          isStarred={true}
          onStarChange={(isStarred) => lead.id && handleStarChange(lead.id, isStarred)}
          onMemberChange={onMemberChange}
        />
      ))}

      {/* Pagination */}
      {sortedLeads.length > 0 && (
        <div style={{ marginTop: '48px' }}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            totalItems={sortedLeads.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};